import moment from "moment";
import { Fragment, useEffect, useRef, useState, useCallback } from "react";
import { Helmet } from "react-helmet-async";
import { FiEdit2, FiSearch, FiTrash, FiStar } from "react-icons/fi";
import { useDispatch } from "react-redux";
import ReactPaginate from "react-paginate";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import Card from "../../components/Card";
import swal from "@sweetalert/with-react";
import Button from "../../components/Button";
import Loader from "../../components/Loader";
import Modal from "../../components/Modal";
import Table, { Td, Th } from "../../components/Table";
import { setInterface } from "../../redux/slices/interfaceSlice";
import service from "../../service";
import BigLoader from "../../components/BigLoader";
import sanitizeHtml from "../../utils/sanitizeHtml";

moment.locale("id");

export default function Review() {
  const dispatch = useDispatch();
  const [data, setData] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [editItem, setEditItem] = useState(null);
  const [editStar, setEditStar] = useState(0);
  const [editBody, setEditBody] = useState("");
  const [editSaving, setEditSaving] = useState(false);
  const searchRef = useRef();
  const _modalEdit = useRef();
  const debounceRef = useRef();

  dispatch(
    setInterface({
      active: "Reviews",
      pageTitle: "Reviews",
    })
  );

  const getData = useCallback(() => {
    setLoading(true);
    service
      .get("/api/admin/reviews", { params: { page, search } })
      .then((response) => {
        const { data, meta } = response.data;
        setData(data);
        setTotal(meta?.last_page || 0);
        setLoading(false);
      })
      .catch(() => {
        setLoading(false);
      });
  }, [page, search]);

  useEffect(() => {
    getData();
  }, [getData]);

  const onSearchChange = (e) => {
    const value = e.target.value;
    setSearch(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setPage(1);
    }, 300);
  };

  const openEdit = (item) => {
    setEditItem(item);
    setEditStar(item.star);
    setEditBody(item.body || "");
    _modalEdit.current?.show();
  };

  const saveEdit = () => {
    if (!editItem || !editStar) return;
    setEditSaving(true);
    service
      .put("/api/admin/reviews/" + editItem.id, {
        star: editStar,
        body: editBody,
      })
      .then(() => {
        setEditSaving(false);
        swal("Berhasil", "Review telah diperbarui", "success");
        _modalEdit.current?.hide();
        getData();
      })
      .catch(() => {
        setEditSaving(false);
      });
  };

  const deleteReview = (item) => {
    swal({
      title: "Hapus Review?",
      text: `Review oleh ${item.user_fullname}`,
      icon: "warning",
      buttons: ["Batal", "Hapus"],
      dangerMode: true,
    }).then((confirmed) => {
      if (!confirmed) return;
      setLoading(true);
      service
        .delete("/api/admin/reviews/" + item.id)
        .then(() => {
          setLoading(false);
          swal("Berhasil", "Review telah dihapus", "success");
          getData();
        })
        .catch(() => {
          setLoading(false);
        });
    });
  };

  return (
    <Card>
      <Helmet>
        <title>Reviews</title>
      </Helmet>
      <div className="mb-4 h-11 flex items-center bg-white-100 border border-white-300 rounded-sm transition-colors focus-within:border-primary-500 focus-within:ring-2 focus-within:ring-primary-100">
        <span className="ml-3 text-black-400">
          <FiSearch />
        </span>
        <input
          ref={searchRef}
          className="flex-1 h-full px-3 rounded-sm focus:outline-none bg-transparent"
          placeholder="Cari user, review, atau judul..."
          defaultValue={search}
          onChange={onSearchChange}
        />
      </div>
      {loading ? (
        <div className="h-64 flex justify-center items-center flex-col">
          <Loader className="bg-black-100" />
          <span className="mt-3 text-black-300 text-sm">Memuat</span>
        </div>
      ) : data.length ? (
        <Fragment>
          <Table>
            <thead>
              <tr>
                <Th>User</Th>
                <Th>Judul</Th>
                <Th>Rating</Th>
                <Th>Review</Th>
                <Th>Tanggal</Th>
                <Th className="w-24">Aksi</Th>
              </tr>
            </thead>
            <tbody>
              {data.map((item) => (
                <tr key={item.id}>
                  <Td className="max-w-[120px] truncate" title={item.user_fullname || ""}>
                    {item.user_fullname || "-"}
                  </Td>
                  <Td className="max-w-[150px] truncate" title={item.course_title || item.deep_read_title || ""}>
                    {item.course_title || item.deep_read_title || "-"}
                  </Td>
                  <Td>
                    <div className="flex text-yellow-200">
                      {[1, 2, 3, 4, 5].map((s) => (
                        <span
                          key={s}
                          className={s <= item.star ? "" : "text-white-400"}
                        >
                          <FiStar
                            fill={s <= item.star ? "currentColor" : "none"}
                          />
                        </span>
                      ))}
                    </div>
                  </Td>
                  <Td className="max-w-[200px] truncate" title={item.body ? item.body.replace(/<[^>]+>/g, '') : ""}>
                    {item.body ? (
                      <span
                        dangerouslySetInnerHTML={{ __html: sanitizeHtml(item.body.length > 30 ? item.body.substring(0, 30) + "..." : item.body, { ALLOWED_TAGS: ['b','i','u','ul','ol','li','br','p','em','strong','span'], ALLOWED_ATTR: [] }) }}
                      />
                    ) : "-"}
                  </Td>
                  <Td className="text-sm whitespace-nowrap">
                    {item.created_at
                      ? moment(item.created_at).format("DD/MM/YY")
                      : "-"}
                  </Td>
                  <Td>
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={() => openEdit(item)}
                        className="p-1.5 rounded-sm border border-white-300 text-sm transition-colors hover:bg-yellow-200 hover:border-yellow-300 flex items-center"
                      >
                        <FiEdit2 />
                      </button>
                      <button
                        type="button"
                        onClick={() => deleteReview(item)}
                        className="p-1.5 rounded-sm border border-white-300 text-sm transition-colors hover:bg-red-200 hover:border-red-300 hover:text-white-100 flex items-center"
                      >
                        <FiTrash />
                      </button>
                    </div>
                  </Td>
                </tr>
              ))}
            </tbody>
          </Table>
          <ReactPaginate
            pageCount={total}
            pageRangeDisplayed={5}
            marginPagesDisplayed={1}
            initialPage={page - 1}
            containerClassName="flex flex-wrap mt-6 gap-1"
            previousLinkClassName="border border-white-300 rounded-sm py-1.5 px-3 text-sm transition-colors hover:bg-primary-500 hover:border-primary-600 hover:text-white-100 block"
            nextLinkClassName="border border-white-300 rounded-sm py-1.5 px-3 text-sm transition-colors hover:bg-primary-500 hover:border-primary-600 hover:text-white-100 block"
            pageLinkClassName="border border-white-300 rounded-sm py-1.5 px-3 text-sm transition-colors hover:bg-primary-500 hover:border-primary-600 hover:text-white-100 block"
            activeLinkClassName="bg-primary-500 border-primary-600 text-white-100"
            onPageChange={({ selected }) => setPage(selected + 1)}
          />
        </Fragment>
      ) : (
        <div className="text-center flex flex-col justify-center items-center h-40 text-black-300">
          <p>Belum ada review</p>
        </div>
      )}

      <Modal ref={_modalEdit} title="Edit Review">
        {editItem && (
          <div>
            <div className="mb-3">
              <label className="text-black-200 font-semibold text-sm mb-1 block">
                User
              </label>
              <p className="text-black-300">{editItem.user_fullname}</p>
            </div>
            <div className="mb-3">
              <label className="text-black-200 font-semibold text-sm mb-1 block">
                Rating
              </label>
              <div className="flex text-3xl">
                {[1, 2, 3, 4, 5].map((s) => (
                  <button
                    key={s}
                    type="button"
                    className={`mr-2 transition-colors ${
                      editStar >= s ? "text-yellow-200" : "text-white-400"
                    }`}
                    onClick={() => setEditStar(s)}
                  >
                    <FiStar fill={editStar >= s ? "currentColor" : "none"} />
                  </button>
                ))}
              </div>
            </div>
            <div className="mb-3">
              <label className="text-black-200 font-semibold text-sm mb-1 block">
                Review
              </label>
              <ReactQuill
                value={editBody}
                onChange={setEditBody}
                modules={{
                  toolbar: [
                    [{ header: [false, 1, 2, 3] }],
                    ['bold', 'italic', 'underline'],
                    [{ list: 'ordered' }, { list: 'bullet' }],
                  ],
                }}
              />
            </div>
            <div className="flex gap-2">
              <Button type="button" onClick={saveEdit}>
                {editSaving ? "Menyimpan..." : "Simpan"}
              </Button>
              <Button
                type="button"
                color="white"
                onClick={() => _modalEdit.current?.hide()}
              >
                Batal
              </Button>
            </div>
          </div>
        )}
      </Modal>

      {editSaving && <BigLoader />}
    </Card>
  );
}
